import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queryKeys } from '@/api/queryKeys';
import { searchUsersTypeahead } from '@/features/search/api';
import type { SearchUserResult } from '@/features/search/types';

const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_PAGE_SIZE = 100;
const MIN_QUERY_LENGTH = 1;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface UseUserTypeaheadOptions {
  readonly query: string;
  readonly debounceMs?: number;
  readonly size?: number;
  readonly enabled?: boolean;
}

export interface UseUserTypeaheadResult {
  readonly query: string;
  readonly results: readonly SearchUserResult[];
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
}

/**
 * Debounced React Query hook for the user typeahead endpoint
 * (`GET /userservice/api/v3/user/typeahed`).
 */
export function useUserTypeaheadQuery({
  query,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  size = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseUserTypeaheadOptions): UseUserTypeaheadResult {
  const debounced = useDebouncedValue(query.trim(), debounceMs);
  const isQueryable = debounced.length >= MIN_QUERY_LENGTH;

  const queryResult = useQuery({
    queryKey: queryKeys.search.users(debounced, 0, size),
    queryFn: ({ signal }) => searchUsersTypeahead({ query: debounced, page: 0, size, signal }),
    enabled: enabled && isQueryable,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return {
    query: debounced,
    results: isQueryable ? (queryResult.data ?? []) : [],
    isLoading: isQueryable && queryResult.isLoading,
    isFetching: isQueryable && queryResult.isFetching,
    isError: queryResult.isError,
  };
}
